/*---------------------------------------------------------------------------------------------
 * Copyright (c) Bentley Systems, Incorporated. All rights reserved.
 * See LICENSE.md in the project root for license terms and full copyright notice.
 *--------------------------------------------------------------------------------------------*/
import * as React from 'react';
import cx from 'classnames';
import { useFloating, useClick, useDismiss, useInteractions, size, autoUpdate, offset, flip, shift, autoPlacement, inline, hide, FloatingFocusManager, useHover, useFocus, safePolygon, useRole, FloatingPortal, } from '@floating-ui/react';
import { Box, cloneElementWithRef, useControlledState, useId, useLayoutEffect, useMergedRefs, } from '../../utils/index.js';
import { Portal } from '../../utils/components/Portal.js';
import { ThemeProvider } from '../ThemeProvider/ThemeProvider.js';
// ----------------------------------------------------------------------------
export const usePopover = (options) => {
    const { placement = 'bottom-start', visible, onVisibleChange, closeOnOutsideClick, autoUpdateOptions, matchWidth, trigger = { click: true, hover: false, focus: false }, role, } = options;
    const middleware = { flip: true, shift: true, ...options.middleware };
    const [open, onOpenChange] = useControlledState(false, visible, onVisibleChange);
    const floating = useFloating({
        placement,
        open,
        onOpenChange,
        whileElementsMounted: (...args) => autoUpdate(...args, autoUpdateOptions),
        middleware: [
            middleware.offset !== undefined && offset(middleware.offset),
            middleware.flip && flip(),
            middleware.shift && shift(),
            matchWidth &&
                size({
                    apply: ({ rects }) => {
                        setReferenceWidth(rects.reference.width);
                    },
                }),
            middleware.autoPlacement && autoPlacement(),
            middleware.inline && inline(),
            middleware.hide && hide(),
        ].filter(Boolean),
    });
    const interactions = useInteractions([
        useClick(floating.context, { enabled: !!trigger.click }),
        useDismiss(floating.context, { outsidePress: closeOnOutsideClick }),
        useHover(floating.context, {
            enabled: !!trigger.hover,
            delay: 100,
            handleClose: safePolygon({ buffer: 1 }),
        }),
        useFocus(floating.context, { enabled: !!trigger.focus }),
        useRole(floating.context, { role: 'dialog', enabled: !!role }),
    ]);
    const [referenceWidth, setReferenceWidth] = React.useState();
    const getFloatingProps = React.useCallback((userProps) => interactions.getFloatingProps({
        ...userProps,
        style: {
            ...floating.floatingStyles,
            zIndex: 9999,
            ...(matchWidth && referenceWidth
                ? {
                    minInlineSize: `${referenceWidth}px`,
                    maxInlineSize: `min(${referenceWidth * 2}px, 90vw)`,
                }
                : {}),
            ...userProps?.style,
        },
    }), [floating.floatingStyles, interactions, matchWidth, referenceWidth]);
    return React.useMemo(() => ({
        open,
        onOpenChange,
        ...interactions,
        getFloatingProps,
        ...floating,
    }), [open, onOpenChange, interactions, getFloatingProps, floating]);
};
/**
 * A utility component to help with positioning of floating content relative to a trigger.
 * Built on top of [`floating-ui`](https://floating-ui.com/).
 *
 * @see https://itwinui.bentley.com/docs/popover
 *
 * @example
 * <Popover content='This is a popover'>
 *   <Button>Show popover</Button>
 * </Popover>
 */
export const Popover = React.forwardRef((props, forwardedRef) => {
    const { portal = true, 
    //
    // popover options
    visible, placement = 'bottom-start', onVisibleChange, closeOnOutsideClick = true, middleware, 
    //
    // extra props
    positionReference, 
    //
    // dom props
    className, children, content, applyBackground = false, ...rest } = props;
    const popover = usePopover({
        visible,
        placement,
        onVisibleChange,
        closeOnOutsideClick,
        role: 'dialog',
        middleware,
    });
    const [popoverElement, setPopoverElement] = React.useState();
    const popoverRef = useMergedRefs(popover.refs.setFloating, forwardedRef, setPopoverElement);
    const triggerId = `${useId()}-trigger`;
    const hasAriaLabel = !!props['aria-labelledby'] || !!props['aria-label'];
    useLayoutEffect(() => {
        if (!positionReference) {
            return;
        }
        popover.refs.setPositionReference(positionReference);
        return () => void popover.refs.setPositionReference(null);
    }, [popover.refs, positionReference]);
    return (React.createElement(React.Fragment, null,
        cloneElementWithRef(children, (children) => ({
            id: children.props.id || triggerId,
            ...popover.getReferenceProps(children.props),
            ref: popover.refs.setReference,
        })),
        popover.open ? (React.createElement(Portal, { portal: portal },
            React.createElement(FloatingPortal, null,
                React.createElement(ThemeProvider, { portalContainer: popoverElement },
                    React.createElement(FloatingFocusManager, { context: popover.context, modal: false, initialFocus: popover.refs.floating },
                        React.createElement(Box, { className: cx({ 'iui-popover-surface': applyBackground }, className), "aria-labelledby": !hasAriaLabel
                                ? popover.refs.domReference.current?.id
                                : undefined, ...popover.getFloatingProps(rest), ref: popoverRef }, content)))))) : null));
});
